import { FC, useState, useMemo } from "react";
import { Box, Typography, Button, InputBase, Collapse, IconButton } from "@mui/material";
import { FiPlus, FiSearch, FiChevronDown, FiChevronRight, FiMoreVertical } from "react-icons/fi";
import { UIChatSession } from "@/types/chat";
import ChatListItem from "./ChatListItem";

interface SidebarProps {
  isOpen: boolean;
  width: string;
  groupedSessions: { label: string; items: UIChatSession[] }[];
  selectedChatId: string | null;
  onChatSelect: (id: string) => void;
  onNewChat: () => void;
  isLoading: boolean;
}

const Sidebar: FC<SidebarProps> = ({
  isOpen,
  width,
  groupedSessions,
  selectedChatId,
  onChatSelect,
  onNewChat,
  isLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const filteredGroupedSessions = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedSessions;
    }
    const lowerQuery = searchQuery.toLowerCase();
    return groupedSessions.map(group => ({
      ...group,
      items: group.items.filter(item => (item.title || "Untitled Chat").toLowerCase().includes(lowerQuery))
    })).filter(group => group.items.length > 0);
  }, [groupedSessions, searchQuery]);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: width,
        height: "100%",
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #EBEBEB",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: "18px", pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "20px",
            color: "#031112",
            letterSpacing: "-0.2px",
          }}
        >
          Riffs Intelligence
        </Typography>
        <IconButton
          size="small"
          sx={{
            width: 16,
            height: 16,
            color: "#AEB3B3",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#8E9494",
            },
          }}
        >
          <FiMoreVertical size={16} />
        </IconButton>
      </Box>

      {/* Search Bar */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            border: "1px solid #EBEBEB",
            borderRadius: "8px",
            pl: "10px",
            pr: "10px",
            py: "8px",
            height: "31px",
          }}
        >
          <FiSearch color="#C0C4C4" size={16} style={{ marginRight: 8 }} />
          <InputBase
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              fontSize: "14px",
              fontFamily: "Inter, sans-serif",
              color: "#031112",
              "& ::placeholder": { color: "#C0C4C4", opacity: 1 },
            }}
          />
        </Box>
      </Box>

      {/* New Chat Button */}
      <Box sx={{ px: 2, pb: "16px" }}>
        <Button
            onClick={onNewChat}
            disabled={isLoading}
            fullWidth
            sx={{
                background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, #0B494B 0%, #0B494B 100%)",
                borderRadius: "8px",
                textTransform: "none",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                boxShadow: "none",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                pl: "6px",
                pr: "12px",
                py: "6px",
                "&:hover": {
                    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, #093B3D 0%, #093B3D 100%)",
                },
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    boxShadow: "inset 0px 4px 46px 0px rgba(255,255,255,0.16)",
                }}
            />
            <FiPlus color="#FFFFFF" size={18} />
            <Typography 
              sx={{ 
                color: "#FFFFFF", 
                fontWeight: 500, 
                fontSize: "14px",
                fontFamily: "Inter Display, sans-serif",
                letterSpacing: "-0.14px",
              }}
            >
              New Chat
            </Typography>
        </Button>
      </Box>

      {/* History Content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "13px", py: "16px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "16px", opacity: 0.8, px: "8px" }}>
            <Typography sx={{ fontFamily: "Inter Display, sans-serif", fontSize: "14px", fontWeight: 600, color: "#001121" }}>
                History
            </Typography>
            <IconButton
              size="small"
              sx={{
                width: 16,
                height: 16,
                color: "#AEB3B3",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#8E9494",
                },
              }}
            >
              <FiMoreVertical size={16} />
            </IconButton>
        </Box>

        {filteredGroupedSessions.map((group) => (
          <Box key={group.label} sx={{ mb: "12px" }}>
            <Box 
                sx={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    mb: "12px",
                    px: "8px",
                    cursor: 'pointer',
                    userSelect: 'none',
                    position: "relative",
                    zIndex: 2,
                }}
                onClick={() => toggleGroup(group.label)}
            >
                <Typography
                sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8E9494",
                    fontFamily: "Inter Display, sans-serif",
                    lineHeight: "20px",
                }}
                >
                {group.label}
                </Typography>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  {collapsedGroups[group.label] ? (
                    <FiChevronRight color="#7F8C99" size={20} />
                  ) : (
                    <FiChevronDown color="#7F8C99" size={20} />
                  )}
                </Box>
            </Box>
            <Collapse in={!collapsedGroups[group.label]}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {group.items.map((chat, itemIndex) => (
                    <ChatListItem
                    key={chat.id}
                    id={chat.id}
                    title={chat.title || "Untitled Chat"}
                    timestamp={chat.timestamp}
                    isSelected={chat.id === selectedChatId}
                    onClick={onChatSelect}
                    isFirst={itemIndex === 0}
                    isLast={itemIndex === group.items.length - 1}
                    />
                ))}
                </Box>
            </Collapse>
          </Box>
        ))}
        {filteredGroupedSessions.length === 0 && searchQuery && (
            <Typography sx={{ textAlign: 'center', mt: 4, color: '#8E9494', fontSize: '14px' }}>
                No chats found
            </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;

